import { useState, useMemo } from 'react';
import {
  Dialog, DialogSurface, DialogBody, DialogContent,
  Button, Input, Badge, makeStyles, tokens, Card, TabList, Tab,
} from '@fluentui/react-components';
import {
  Dismiss24Regular, Search24Regular,
  Desktop24Regular, Database24Regular,
  Copy24Regular, Checkmark24Regular, BookOpen24Regular,
  Person24Regular, Bot24Regular, LeafTwo24Regular,
  MoneyHand24Regular,
  VehicleTruck24Regular, ShieldCheckmark24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { useT } from '../../i18n';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
export interface SystemGuideModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: string;
  /** Reactiva el product tour guiado (driver.js) — antes vivía en un botón "?"
   *  aparte en el TopBar, duplicado visualmente junto al de esta guía. */
  onStartTour?: () => void;
}

// ---------------------------------------------------------------------------
// Module metadata (no text here: names, descriptions and examples live in i18n
// under systemGuide.content.modules[id])
// ---------------------------------------------------------------------------
interface ModuleMeta {
  id: string;
  category: 'core' | 'operations' | 'resources' | 'business' | 'logistics' | 'system';
  roles: string[];
}

const MODULES_META: ModuleMeta[] = [
  { id: 'rancho-lote', category: 'core', roles: ['ADMIN', 'SUPER_ADMIN'] },
  { id: 'operations', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'irrigation', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'harvest', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'crop-management', category: 'operations', roles: ['AGRONOMIST', 'SUPERVISOR', 'ADMIN'] },
  { id: 'inventory', category: 'resources', roles: ['WAREHOUSE_MANAGER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'machinery', category: 'resources', roles: ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'payroll', category: 'resources', roles: ['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'] },
  { id: 'finance', category: 'business', roles: ['AGRONOMIST', 'ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'] },
  { id: 'protocol', category: 'operations', roles: ['AGRONOMIST', 'ADMIN', 'SUPER_ADMIN'] },
  { id: 'merma', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'ADMIN', 'AGRONOMIST'] },
  { id: 'export', category: 'logistics', roles: ['SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'] },
  { id: 'transport', category: 'logistics', roles: ['SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'] },
  { id: 'traceability', category: 'logistics', roles: ['AGRONOMIST', 'SUPERVISOR', 'ADMIN', 'SUPER_ADMIN'] },
  { id: 'porcino', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'ADMIN', 'FINANCE_ADMIN'] },
  { id: 'resources', category: 'resources', roles: ['AGRONOMIST', 'SUPERVISOR', 'ADMIN'] },
  { id: 'drone', category: 'operations', roles: ['FIELD_WORKER', 'SUPERVISOR', 'AGRONOMIST', 'ADMIN'] },
  { id: 'calendar', category: 'core', roles: ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'FIELD_WORKER'] },
  { id: 'weather', category: 'core', roles: ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'FIELD_WORKER'] },
  { id: 'users', category: 'system', roles: ['SUPER_ADMIN', 'ADMIN'] },
  { id: 'vocabulary', category: 'system', roles: ['ADMIN', 'AGRONOMIST', 'SUPERVISOR'] },
  { id: 'sales', category: 'business', roles: ['ADMIN', 'SUPER_ADMIN', 'SUPERVISOR'] },
  { id: 'support', category: 'system', roles: ['SUPER_ADMIN', 'ADMIN', 'AGRONOMIST', 'SUPERVISOR', 'FIELD_WORKER'] },
  { id: 'developer', category: 'system', roles: ['ADMIN', 'DEVELOPER'] },
];

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  surface: {
    maxWidth: '1000px',
    width: '95vw',
    maxHeight: '90vh',
    padding: '0',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  header: {
    padding: '20px 24px 12px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleText: {
    fontSize: '20px',
    fontWeight: '700',
    color: tokens.colorNeutralForeground1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  subtitleText: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  searchBar: {
    width: '100%',
  },
  roleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
  },
  content: {
    padding: '20px 24px',
    overflowY: 'auto',
    maxHeight: 'calc(90vh - 140px)',
  },

  // Dual entry visual cards
  dualGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
    gap: '16px',
    marginBottom: '24px',
    '@media (max-width: 640px)': {
      gridTemplateColumns: '1fr',
    },
  },
  dualCard: {
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  dualHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: '600',
    fontSize: '15px',
    color: tokens.colorBrandForeground1,
  },
  commonDbCard: {
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  // Modules grid
  modulesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
  },
  moduleCard: {
    padding: '14px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '10px',
    ':hover': {
      border: `1px solid ${tokens.colorBrandStroke1}`,
      boxShadow: tokens.shadow4,
    },
  },
  moduleTitle: {
    fontWeight: '700',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
  },
  moduleDesc: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    lineHeight: '1.4',
  },
  codeSnippet: {
    fontSize: '11px',
    fontFamily: 'monospace',
    padding: '4px 8px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorBrandForeground1,
    overflowX: 'auto',
  },

  // Voice Cheatsheet
  cheatsheetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: '16px',
  },
  cheatCard: {
    padding: '16px',
    borderRadius: '8px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground2,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  cheatPhrase: {
    fontSize: '13px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    padding: '8px',
    borderRadius: '4px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
  },
  cheatFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '4px',
  },

  // FAQ
  faqSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  faqItem: {
    padding: '14px',
    borderRadius: '8px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderLeft: `4px solid ${tokens.colorBrandBackground}`,
  },
  faqQuestion: {
    fontWeight: '700',
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  faqAnswer: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function SystemGuideModal({ open, onOpenChange, initialTab = 'architecture', onStartTour }: SystemGuideModalProps) {
  const s = useStyles();
  const t = useT();
  const g = t.systemGuide;
  const c = g.content;

  const [activeTab, setActiveTab] = useState<string>(initialTab);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('ALL');
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  // Cierra la guía y arranca el product tour guiado (driver.js) sobre el Sidebar.
  const handleStartTour = () => {
    onOpenChange(false);
    onStartTour?.();
  };

  // Copiar al portapapeles
  const handleCopyPhrase = (phrase: string, index: number) => {
    void navigator.clipboard.writeText(phrase);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Filtrado de módulos en tiempo real (sobre el texto ya traducido)
  const filteredModules = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return MODULES_META.filter((mod) => {
      const text = c.modules[mod.id as keyof typeof c.modules] as { name: string; description: string; example?: string };
      const matchesSearch =
        searchTerm === '' ||
        text.name.toLowerCase().includes(q) ||
        text.description.toLowerCase().includes(q) ||
        mod.id.toLowerCase().includes(q) ||
        (text.example && text.example.toLowerCase().includes(q));

      const matchesRole =
        selectedRoleFilter === 'ALL' || mod.roles.includes(selectedRoleFilter);

      return matchesSearch && matchesRole;
    });
  }, [searchTerm, selectedRoleFilter, c]);

  // Filtrado de comandos de voz
  const filteredCheatsheet = useMemo(() => {
    return c.voiceExamples.filter((item) => {
      return (
        searchTerm === '' ||
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.phrase.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
  }, [searchTerm, c]);

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface className={s.surface}>
        <DialogBody style={{ padding: 0 }}>

          {/* Header */}
          <div className={s.header}>
            <div className={s.titleRow}>
              <div className={s.titleText}>
                <BookOpen24Regular style={{ color: tokens.colorBrandForeground1 }} />
                <span>{g.title}</span>
              </div>
              <Button
                appearance="subtle"
                icon={<Dismiss24Regular />}
                onClick={() => onOpenChange(false)}
                aria-label={t.a11yLabels.close}
              />
            </div>
            <span className={s.subtitleText}>
              {g.subtitle}
            </span>

            {/* Search Input */}
            <Input
              className={s.searchBar}
              contentBefore={<Search24Regular />}
              placeholder={g.searchPlaceholder}
              value={searchTerm}
              onChange={(_, data) => setSearchTerm(data.value)}
            />

            {/* Navigation Tabs */}
            <TabList
              selectedValue={activeTab}
              onTabSelect={(_, data) => setActiveTab(data.value as string)}
            >
              <Tab value="architecture">🚀 {g.tabs.architecture}</Tab>
              <Tab value="modules">📦 {g.tabs.modules}</Tab>
              <Tab value="voiceCommands">🎙️ {g.tabs.voiceCommands}</Tab>
              <Tab value="roles">👤 {g.tabs.roles}</Tab>
              <Tab value="faq">❓ {g.tabs.faq}</Tab>
            </TabList>
          </div>

          {/* Body Content */}
          <DialogContent className={s.content}>

            {/* TAB 1: ARQUITECTURA Y PATRÓN DE DOBLE ENTRADA */}
            {activeTab === 'architecture' && (
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '12px' }}>
                  {c.archTitle}
                </h3>
                <p style={{ fontSize: '13px', color: tokens.colorNeutralForeground2, marginBottom: '16px' }}>
                  {c.archIntro}
                </p>

                <div className={s.dualGrid}>
                  <div className={s.dualCard}>
                    <div className={s.dualHeader}>
                      <Bot24Regular fontSize={20} />
                      <span>{g.dualEntry.telegramTitle}</span>
                    </div>
                    <p style={{ fontSize: '12px', margin: 0, color: tokens.colorNeutralForeground2 }}>
                      {g.dualEntry.telegramDesc}
                    </p>
                    <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                      ⚡ {c.draftGateBefore} <strong>{c.draftGateTerm}</strong>: {c.draftGateAsk} <em>"{c.draftGateQuestion}"</em> {c.draftGateAfter}
                    </span>
                  </div>

                  <div className={s.dualCard}>
                    <div className={s.dualHeader}>
                      <Desktop24Regular fontSize={20} />
                      <span>{g.dualEntry.webTitle}</span>
                    </div>
                    <p style={{ fontSize: '12px', margin: 0, color: tokens.colorNeutralForeground2 }}>
                      {g.dualEntry.webDesc}
                    </p>
                    <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                      {c.webHint}
                    </span>
                  </div>
                </div>

                <div className={s.commonDbCard}>
                  <Database24Regular fontSize={24} style={{ color: tokens.colorBrandForeground1 }} />
                  <div>
                    <div style={{ fontWeight: '700', fontSize: '13px' }}>
                      {g.dualEntry.commonDbTitle}
                    </div>
                    <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
                      {g.dualEntry.commonDbDesc}
                    </div>
                  </div>
                </div>

                {onStartTour && (
                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      appearance="secondary"
                      icon={<ArrowClockwise24Regular />}
                      onClick={handleStartTour}
                    >
                      {t.topBar.restartTourLabel}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* TAB 2: MÓDULOS DEL SISTEMA */}
            {activeTab === 'modules' && (
              <div>
                {/* Role Filter Chips */}
                <div className={s.roleContainer} style={{ marginBottom: '16px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600' }}>
                    {g.filterRole}
                  </span>
                  {['ALL', 'FIELD_WORKER', 'AGRONOMIST', 'SUPERVISOR', 'ADMIN', 'INVESTOR'].map((roleKey) => (
                    <Button
                      key={roleKey}
                      size="small"
                      appearance={selectedRoleFilter === roleKey ? 'primary' : 'subtle'}
                      onClick={() => setSelectedRoleFilter(roleKey)}
                    >
                      {roleKey === 'ALL'
                        ? g.allRoles
                        : roleKey}
                    </Button>
                  ))}
                </div>

                {/* Modules Grid */}
                <div className={s.modulesGrid}>
                  {filteredModules.map((mod) => {
                    const text = c.modules[mod.id as keyof typeof c.modules] as { name: string; description: string; example?: string };
                    return (
                      <div key={mod.id} className={s.moduleCard}>
                        <div>
                          <div style={{ marginBottom: '4px' }}>
                            <span className={s.moduleTitle}>{text.name}</span>
                          </div>
                          <p className={s.moduleDesc}>{text.description}</p>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {text.example && (
                            <div className={s.codeSnippet}>
                              🗣️ {c.botLabel} "{text.example}"
                            </div>
                          )}
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                            {mod.roles.map((r) => (
                              <Badge key={r} size="small" appearance="tint" color="subtle">{r}</Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TAB 3: COMANDOS DE VOZ TELEGRAM */}
            {activeTab === 'voiceCommands' && (
              <div>
                <p style={{ fontSize: '13px', color: tokens.colorNeutralForeground2, marginBottom: '16px' }}>
                  {c.cheatsheetIntroPre} <strong>{c.cheatsheetIntroBtn}</strong> {c.cheatsheetIntroPost}
                </p>

                <div className={s.cheatsheetGrid}>
                  {filteredCheatsheet.map((item, idx) => (
                    <div key={idx} className={s.cheatCard}>
                      <div style={{ fontWeight: '700', fontSize: '14px' }}>{item.title}</div>
                      <div className={s.cheatPhrase}>{item.phrase}</div>
                      <div style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                        💡 {item.note}
                      </div>

                      <div className={s.cheatFooter}>
                        <Badge size="small" color="informative" appearance="tint">{item.role}</Badge>
                        <Button
                          size="small"
                          appearance="secondary"
                          icon={copiedIndex === idx ? <Checkmark24Regular /> : <Copy24Regular />}
                          onClick={() => handleCopyPhrase(item.phrase, idx)}
                        >
                          {copiedIndex === idx
                            ? g.copied
                            : g.copyBtn}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* TAB 4: GUÍAS POR ROL */}
            {activeTab === 'roles' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {[
                  {
                    role: 'FIELD_WORKER',
                    name: g.rolesList.FIELD_WORKER,
                    icon: <Person24Regular />,
                    desc: g.rolesDesc.FIELD_WORKER,
                    // PENDIENTE i18n: lista de tareas por rol (decisión de producto pendiente de traducir)
                    tasks: ['Enviar notas de voz de riego/aplicación por Telegram', 'Responder "Sí" para confirmar registros borrador', 'Consultar tareas asignadas para el día'],
                  },
                  {
                    role: 'AGRONOMIST',
                    name: g.rolesList.AGRONOMIST,
                    icon: <LeafTwo24Regular />,
                    desc: g.rolesDesc.AGRONOMIST,
                    tasks: ['Monitorear scouting de plagas y mapas de presión', 'Validar fechas de cosecha segura (PHI) por lote', 'Cargar protocolos fitosanitarios de la temporada'],
                  },
                  {
                    role: 'SUPERVISOR',
                    name: g.rolesList.SUPERVISOR,
                    icon: <VehicleTruck24Regular />,
                    desc: g.rolesDesc.SUPERVISOR,
                    tasks: ['Iniciar y cerrar cronómetros de maquinaria con horómetro', 'Controlar asistencia y pago por destajo/jornal', 'Solicitar despachos de insumos a bodega central'],
                  },
                  {
                    role: 'ADMIN',
                    name: g.rolesList.ADMIN,
                    icon: <ShieldCheckmark24Regular />,
                    desc: g.rolesDesc.ADMIN,
                    tasks: ['Aprobar salidas de inventario y compras de agroquímicos', 'Cerrar periodos quincenales de nómina', 'Auditar P&L y margen de utilidad por hectárea'],
                  },
                  {
                    role: 'INVESTOR',
                    name: g.rolesList.INVESTOR,
                    icon: <MoneyHand24Regular />,
                    desc: g.rolesDesc.INVESTOR,
                    tasks: ['Consultar resumen ejecutivo (/investor/overview)', 'Revisar rendimiento histórico t/ha y empaque', 'Verificar trazabilidad de envíos de exportación'],
                  },
                ].map((r) => (
                  <Card key={r.role} style={{ padding: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '700', fontSize: '15px' }}>
                      {r.icon}
                      <span>{r.name}</span>
                      <Badge color="brand" appearance="tint">{r.role}</Badge>
                    </div>
                    <p style={{ fontSize: '13px', color: tokens.colorNeutralForeground2, margin: '6px 0 10px 0' }}>
                      {r.desc}
                    </p>
                    <div style={{ fontSize: '12px', fontWeight: '600', marginBottom: '4px' }}>{c.mainActions}</div>
                    <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: tokens.colorNeutralForeground2 }}>
                      {r.tasks.map((task, i) => (
                        <li key={i}>{task}</li>
                      ))}
                    </ul>
                  </Card>
                ))}
              </div>
            )}

            {/* TAB 5: FAQ & GLOSARIO */}
            {activeTab === 'faq' && (
              <div className={s.faqSection}>
                <h3 style={{ margin: 0 }}>{c.glossaryHeading}</h3>

                <div className={s.faqItem}>
                  <div className={s.faqQuestion}>{c.glossaryQuestions.PHI}</div>
                  <div className={s.faqAnswer}>
                    {g.glossary.PHI}
                    {' '}{c.phiBlockNote}
                  </div>
                </div>

                <div className={s.faqItem}>
                  <div className={s.faqQuestion}>{c.glossaryQuestions.REI}</div>
                  <div className={s.faqAnswer}>
                    {g.glossary.REI}
                  </div>
                </div>

                <div className={s.faqItem}>
                  <div className={s.faqQuestion}>{c.glossaryQuestions.FEFO}</div>
                  <div className={s.faqAnswer}>
                    {g.glossary.FEFO}
                  </div>
                </div>

                <div className={s.faqItem}>
                  <div className={s.faqQuestion}>{c.glossaryQuestions.Packout}</div>
                  <div className={s.faqAnswer}>
                    {g.glossary.Packout}
                  </div>
                </div>

                <div className={s.faqItem}>
                  <div className={s.faqQuestion}>{c.glossaryQuestions.DraftGate}</div>
                  <div className={s.faqAnswer}>
                    {g.glossary.DraftGate}
                  </div>
                </div>
              </div>
            )}

          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
